<script setup lang="ts">
  import { type RuleExpression, useField } from 'vee-validate'
  import { ExclamationCircleIcon } from '@heroicons/vue/24/outline'
  import { type PropType, computed } from 'vue'

  const props = defineProps({
    name: {
      type: String,
      required: true
    },
    id: {
      type: String,
      required: false
    },
    label: {
      type: String,
      required: false
    },
    modelValue: {
      type: String,
      required: false,
      default: '#000000'
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    rules: {
      type: Object as PropType<RuleExpression<any> | undefined>,
      required: false
    },
    dataCy: {
      type: String,
      required: false
    }
  })

  const emit = defineEmits(['update:modelValue', 'input', 'blur'])

  const {
    value,
    errorMessage,
    handleBlur: veeHandleBlur
  } = useField(props.name, props.rules, {
    syncVModel: true
  })

  const handleInput = (event: Event) => {
    const target = event.target as HTMLInputElement
    value.value = target.value
    emit('update:modelValue', target.value)
    emit('input', target.value)
  }

  const handleBlur = (event: FocusEvent) => {
    veeHandleBlur(event)
    emit('blur', event)
  }
</script>

<template>
  <div class="mb-4">
    <label
      v-if="label"
      :for="props.id || props.name"
      class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
    >
      {{ label }}
    </label>
    <div class="flex items-center gap-3">
      <input
        :id="props.id || props.name"
        type="color"
        :value="value"
        :name="props.name"
        :disabled="disabled"
        :data-cy="dataCy"
        class="h-10 w-20 cursor-pointer rounded-lg border border-gray-300 bg-white disabled:cursor-not-allowed
          disabled:opacity-50 dark:border-neutral-600 dark:bg-neutral-700"
        :class="errorMessage ? 'border-red-500' : ''"
        @input="handleInput"
        @blur="handleBlur"
      />
      <input
        type="text"
        :value="value"
        :disabled="disabled"
        class="block flex-1 rounded-lg border px-3 py-2 text-sm text-gray-800 disabled:cursor-not-allowed
          disabled:bg-gray-100 dark:bg-neutral-700 dark:text-gray-100"
        :class="
          errorMessage
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-200 focus:border-purple-600 focus:ring-purple-600 dark:border-neutral-600'
        "
        placeholder="#000000"
        @input="handleInput"
        @blur="handleBlur"
      />
    </div>
    <div
      v-if="errorMessage"
      class="mt-1 flex items-center gap-1 text-xs text-red-600"
    >
      <ExclamationCircleIcon class="h-4 w-4" />
      {{ errorMessage }}
    </div>
  </div>
</template>
